import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, Download, ExternalLink, Pencil, Plus, Send, Trash2, Loader2, FileUp, Dices } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, authed, errMsg } from "@/lib/api";
import { CategoryEditSheet } from "@/components/CategoryEditSheet";
import { CanvaMasterCard } from "@/components/CanvaMasterCard";
import { ImportCatalogDialog } from "@/components/ImportCatalogDialog";
import { ArrangeBar } from "@/components/ArrangeBar";
import { DownloadCatalogButton } from "@/components/DownloadCatalogButton";
import { PriceToggle } from "@/components/PriceToggle";

const Thumb = ({ src, label, testid }) => (
  <div className="flex-1 min-w-0" data-testid={testid}>
    <div className="aspect-[16/9] bg-[var(--line)] overflow-hidden page-shadow">
      <img src={src} alt={label} loading="lazy" className="w-full h-full object-cover" />
    </div>
    <p className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-ink)] mt-2">{label}</p>
  </div>
);

export const CanvaBadge = ({ canva, id }) => {
  if (!canva?.status) return null;
  if (canva.status === "in_progress") return <span className="inline-flex items-center gap-1 text-xs text-[var(--muted-ink)]" data-testid={`canva-status-${id}`}><Loader2 size={12} className="animate-spin" />Sending to Canva</span>;
  if (canva.status === "success") return (
    <span className="flex flex-col gap-1" data-testid={`canva-status-${id}`}>
      {canva.master_status === "success" && <a href={canva.master_edit_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[var(--terracotta)] hover:underline">In “{canva.master_title}” · pages {canva.master_pages?.join("–")} <ExternalLink size={12} /></a>}
      {canva.master_status === "failed" && <span className="text-xs text-red-700" title={canva.master_error}>Couldn't insert into master design</span>}
      <a href={canva.edit_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[var(--muted-ink)] hover:underline">Standalone copy <ExternalLink size={12} /></a>
    </span>
  );
  return <span className="text-xs text-red-700" title={canva.error} data-testid={`canva-status-${id}`}>Canva sync failed</span>;
};

const Spread = ({ p, i, total, onMove, onDelete, onPush, onRename, pv, bespoke }) => {
  const v = `${encodeURIComponent(p.updated_at)}-${encodeURIComponent(pv)}`;
  const [renaming, setRenaming] = useState(false);
  const rename = async () => { setRenaming(true); await onRename(p.id); setRenaming(false); };
  return (
    <div className="grid md:grid-cols-[64px_1fr_260px] gap-6 py-8 border-b border-[var(--line)] rise" style={{ animationDelay: `${i * 60}ms` }} data-testid={`product-row-${p.id}`}>
      <p className="text-3xl font-bold text-[var(--line)]">{String(i + 1).padStart(2, "0")}</p>
      <div className="flex gap-4">
        <Thumb src={authed(`/products/${p.id}/preview/0.png?v=${v}`)} label={`Page ${i * 2 + 3} · Image set`} testid={`product-thumb-images-${p.id}`} />
        <Thumb src={authed(`/products/${p.id}/preview/1.png?v=${v}`)} label={`Page ${i * 2 + 4} · ${bespoke ? "Data sheet" : "Configurations"}`} testid={`product-thumb-config-${p.id}`} />
      </div>
      <div className="flex flex-col gap-3">
        <div>
          <p className="text-xs font-bold tracking-wider">{p.code}</p>
          <h3 className="text-lg font-bold uppercase leading-tight" data-testid={`product-name-${p.id}`}>{p.name}</h3>
          <p className="text-xs text-[var(--muted-ink)] mt-1">{bespoke ? `${p.bespoke?.standard_config || "No standard size"}${p.bespoke?.standard_length_ft ? ` · ${p.bespoke.standard_length_ft} ft` : ""}` : `${p.configurations.length} configurations`}</p>
          <div className="mt-2"><CanvaBadge canva={p.canva} id={p.id} /></div>
        </div>
        <div className="flex flex-wrap gap-1 mt-auto">
          <Button asChild size="sm" variant="outline" data-testid={`edit-product-${p.id}`}><Link to={`/product/${p.id}/edit`}><Pencil size={13} /></Link></Button>
          <Button size="sm" variant="outline" disabled={i === 0} onClick={() => onMove(p.id, "up")} data-testid={`move-up-${p.id}`}><ArrowUp size={13} /></Button>
          <Button size="sm" variant="outline" disabled={i === total - 1} onClick={() => onMove(p.id, "down")} data-testid={`move-down-${p.id}`}><ArrowDown size={13} /></Button>
          <Button asChild size="sm" variant="outline" data-testid={`download-product-${p.id}`}><a href={authed(`/products/${p.id}/pages.pdf`)}><Download size={13} /></a></Button>
          <Button size="sm" variant="outline" onClick={() => onPush(p.id)} data-testid={`push-canva-${p.id}`} title="Send these 2 pages to Canva"><Send size={13} /></Button>
          <Button size="sm" variant="outline" disabled={renaming} onClick={rename} data-testid={`rename-product-${p.id}`} title="Give it a new random name">{renaming ? <Loader2 size={13} className="animate-spin" /> : <Dices size={13} />}</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`delete-product-${p.id}`}><Trash2 size={13} /></Button></AlertDialogTrigger>
            <AlertDialogContent className="bg-[var(--cream)]">
              <AlertDialogHeader><AlertDialogTitle>Remove {p.name}?</AlertDialogTitle>
                <AlertDialogDescription>Its two pages will be removed from the catalog.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete(p.id)} data-testid={`confirm-delete-${p.id}`}>Remove</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
};

export default function CategoryView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cat, setCat] = useState(null);
  const [products, setProducts] = useState([]);
  const [pushing, setPushing] = useState(false);
  const [rev, setRev] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [showPrices, setShowPrices] = useState(true);

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([api.get(`/categories/${id}`), api.get(`/categories/${id}/products`)]);
      setCat(c.data); setProducts(p.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/import/jobs", { params: { category_id: id } }).then((r) => {
      const run = r.data.find((j) => ["queued", "running"].includes(j.status));
      if (run) { setJobId(run.id); setActiveJob(run); }
    });
  }, [id]);
  useEffect(() => {
    if (!jobId) { setActiveJob(null); return; }
    const t = setInterval(() => api.get(`/import/jobs/${jobId}`).then((r) => setActiveJob(r.data)), 3000);
    return () => clearInterval(t);
  }, [jobId]);
  useEffect(() => {
    if (!products.some((p) => p.canva?.status === "in_progress")) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [products, load]);

  const move = async (pid, direction) => { await api.post(`/products/${pid}/move`, { direction }); load(); };
  const del = async (pid) => { await api.delete(`/products/${pid}`); toast.success("Product removed"); load(); };
  const renameOne = async (pid) => {
    try { const { data } = await api.post(`/products/${pid}/rename`); toast.success(`${data.old} → ${data.new}`); }
    catch (e) { toast.error(errMsg(e)); }
    load();
  };
  const pushOne = async (pid) => {
    toast.info("Sending 2 pages to Canva…");
    try {
      const { data } = await api.post(`/canva/push/product/${pid}`);
      data.status === "success" ? toast.success("Pages added to Canva") : toast.error(data.error || "Canva import failed");
    } catch (e) { toast.error(errMsg(e)); }
    load();
  };
  const pushAll = async () => {
    setPushing(true);
    try {
      const { data } = await api.post(`/canva/push/category/${id}`);
      if (data.status === "success") { toast.success("Full catalog is in Canva"); window.open(data.edit_url, "_blank"); }
      else toast.error(data.error || "Canva import failed");
    } catch (e) { toast.error(errMsg(e)); }
    setPushing(false); load();
  };

  if (!cat) return <p className="text-sm text-[var(--muted-ink)]">Loading…</p>;
  const pages = products.length * 2 + 3;
  const v = `${encodeURIComponent(JSON.stringify(cat).length)}-${rev}`;

  return (
    <div>
      <Link to="/" className="text-[11px] tracking-[0.25em] uppercase text-[var(--muted-ink)] hover:text-[var(--ink)]" data-testid="back-to-catalogs">← Catalogs</Link>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mt-4 mb-12 rise">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--muted-ink)] mb-3">{cat.catalog_title} · {cat.edition}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="category-title">{cat.name}</h1>
          <p className="text-sm text-[var(--muted-ink)] mt-3" data-testid="category-page-count">{products.length} products · {pages} pages</p>
          <div className="mt-2"><CanvaBadge canva={cat.canva} id="catalog" /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <CategoryEditSheet cat={cat} onSaved={() => { load(); setRev((r) => r + 1); }} />
          <Button variant="outline" onClick={pushAll} disabled={pushing} data-testid="push-catalog-canva-button">
            {pushing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}Full catalog to Canva
          </Button>
          <PriceToggle onChange={(v) => setShowPrices(v)} />
          <DownloadCatalogButton catId={id} />
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-catalog-button"><FileUp size={14} className="mr-2" />Import catalog / website</Button>
          <Button onClick={() => nav(`/category/${id}/new`)} className="bg-[var(--terracotta)] hover:bg-[#8d4427]" data-testid="add-product-button"><Plus size={14} className="mr-2" />Add product</Button>
        </div>
      </div>

      <ImportCatalogDialog open={importOpen} setOpen={setImportOpen} catId={id} catName={cat.name} jobId={jobId} setJobId={setJobId} onDone={load} />
      {activeJob && ["queued", "running"].includes(activeJob.status) && (
        <button onClick={() => setImportOpen(true)} className="w-full text-left flex items-center gap-3 bg-[var(--ink)] text-[var(--cream)] px-5 py-3 mb-4 text-sm" data-testid="import-running-banner">
          <Loader2 size={14} className="animate-spin" />Importing {activeJob.filename}: {activeJob.step} {activeJob.total ? `(${activeJob.current}/${activeJob.total})` : ""}
        </button>
      )}
      <CanvaMasterCard cat={cat} onChange={load} />

      <section className="grid md:grid-cols-[64px_1fr_260px] gap-6 pb-8 border-b border-[var(--line)]">
        <p className="text-[11px] tracking-[0.25em] uppercase text-[var(--muted-ink)] pt-1">Open</p>
        <div className="flex gap-4">
          <Thumb src={authed(`/categories/${id}/preview/cover.png?v=${v}`)} label="Page 1 · Cover" testid="cover-thumb" />
          <Thumb src={authed(`/categories/${id}/preview/intro.png?v=${v}`)} label="Page 2 · Introduction" testid="intro-thumb" />
        </div>
      </section>

      {products.length === 0 && (
        <div className="py-16 text-center border-b border-[var(--line)]" data-testid="empty-products">
          <p className="text-lg font-semibold">No products yet</p>
          <p className="text-sm text-[var(--muted-ink)] mt-2">Add your first product to generate its two catalog pages.</p>
        </div>
      )}
      {products.length > 0 && <ArrangeBar key={`${cat.layout_id}-${rev}`} catId={id} products={products} onChange={load} relayout={cat.relayout} />}
      {products.map((p, i) => <Spread key={p.id} p={p} i={i} total={products.length} onMove={move} onDelete={del} onPush={pushOne} onRename={renameOne} pv={`${showPrices ? 1 : 0}-${cat.brand_name || ""}-${cat.layout_id || ""}-${cat.style || ""}-${rev}`} bespoke={cat.style === "bespoke"} />)}

      <section className="grid md:grid-cols-[64px_1fr_260px] gap-6 py-8">
        <p className="text-[11px] tracking-[0.25em] uppercase text-[var(--muted-ink)] pt-1">Close</p>
        <div className="flex gap-4">
          <Thumb src={authed(`/categories/${id}/preview/closing.png?v=${v}`)} label={`Page ${pages} · Contact`} testid="closing-thumb" />
          <div className="flex-1" />
        </div>
      </section>
    </div>
  );
}
